import { ParsedData, QuestionScores, ModelSummary } from '../types';
import { getModelInferenceStats, getModelConfidenceStats } from './inferenceUtils';
import { ratingToCategory } from './scoreUtils';

export function exportToJSON(data: ParsedData, scores: QuestionScores): string {
  // Enhanced export with computed statistics
  const enhancedData = {
    metadata: {
      exportDate: new Date().toISOString(),
      totalQuestions: data.questions.length,
      models: data.modelNames
    },
    questions: data.questions.map(question => {
      const questionData = {
        id: question.id,
        text: question.text,
        humanAnnotations: question.humanAnnotations?.map(e => ({
          text: e.text,
          label: e.label,
          start: e.start,
          end: e.end
        })),
        models: question.modelResponses.map(response => {
          const entities = response.entities;
          const confidenceScores = entities
            .filter(e => e.score !== undefined)
            .map(e => e.score!);
          
          const avgConfidence = confidenceScores.length > 0
            ? confidenceScores.reduce((sum, s) => sum + s, 0) / confidenceScores.length
            : null;
          
          const score = scores[question.id]?.[response.modelName];
          
          return {
            modelName: response.modelName,
            inferenceTime: response.inferenceTime,
            entityCount: entities.length,
            avgConfidence,
            entities: entities.map(e => ({
              text: e.text,
              label: e.label,
              start: e.start,
              end: e.end,
              confidence: e.score
            })),
            rating: score?.rating,
            notes: score?.notes
          };
        })
      };
      return questionData;
    }),
    modelSummaries: calculateModelSummaries(data, scores)
  };
  
  return JSON.stringify(enhancedData, null, 2);
}

export function exportToCSV(data: ParsedData, scores: QuestionScores): string {
  const headers = [
    'Question ID',
    'Question Text', 
    'Model Name',
    'Inference Time (s)',
    'Entity Count',
    'Entities Found',
    'Entity Positions',
    'Confidence Scores',
    'Avg Confidence',
    'Rating',
    'Notes'
  ];
  const rows: string[] = [headers.join(',')];
  
  data.questions.forEach(question => {
    // Add row for human annotations if they exist
    if (question.humanAnnotations && question.humanAnnotations.length > 0) {
      const humanEntities = question.humanAnnotations;
      const entityTexts = humanEntities.map(e => `${e.text}:${e.label}`).join('; ');
      const entityPositions = humanEntities
        .filter(e => e.start !== undefined && e.end !== undefined)
        .map(e => `(${e.start},${e.end})`)
        .join('; ');
      
      const row = [
        question.id,
        `"${question.text.replace(/"/g, '""')}"`,
      'Annotations',
        '',
        humanEntities.length,
        `"${entityTexts.replace(/"/g, '""')}"`,
        `"${entityPositions}"`,
        '',
        '',
        '',
        ''
      ];
      rows.push(row.join(','));
    }
    
    // Add rows for each model
    data.modelNames.forEach(modelName => {
      const response = question.modelResponses.find(r => r.modelName === modelName);
      const score = scores[question.id]?.[modelName];
      
      // Collect entity information
      const entities = response?.entities || [];
      const entityTexts = entities.map(e => `${e.text}:${e.label}`).join('; ');
      const entityPositions = entities
        .filter(e => e.start !== undefined && e.end !== undefined)
        .map(e => `(${e.start},${e.end})`)
        .join('; ');
      const confidenceScores = entities
        .filter(e => e.score !== undefined)
        .map(e => e.score!.toFixed(4))
        .join('; ');
      const avgConfidence = entities.length > 0 && entities.some(e => e.score !== undefined)
        ? (entities.reduce((sum, e) => sum + (e.score || 0), 0) / entities.filter(e => e.score !== undefined).length).toFixed(4)
        : '';
      
      const row = [
        question.id,
        `"${question.text.replace(/"/g, '""')}"`,
        modelName,
        response?.inferenceTime !== undefined ? response.inferenceTime.toFixed(6) : '',
        entities.length,
        `"${entityTexts.replace(/"/g, '""')}"`,
        `"${entityPositions}"`,
        `"${confidenceScores}"`,
        avgConfidence,
        score?.rating || '',
        `"${(score?.notes || '').replace(/"/g, '""')}"`
      ];
      rows.push(row.join(','));
    });
  });
  
  return rows.join('\n');
}

export function calculateModelSummaries(data: ParsedData, scores: QuestionScores): ModelSummary[] {
  return data.modelNames.map(modelName => {
    const modelScores = data.questions
      .map(q => scores[q.id]?.[modelName])
      .filter(score => score !== undefined && (score as any).rating > 0) as { rating: number }[];

    const totalQuestions = modelScores.length;
    const averageRating = totalQuestions > 0
      ? modelScores.reduce((sum, score) => sum + score.rating, 0) / totalQuestions
      : 0;

    const categoryCounts = {
      Best: 0,
      Good: 0,
      Fair: 0,
      Poor: 0
    };

    modelScores.forEach(score => {
      const derived = ratingToCategory(score.rating);
      categoryCounts[derived]++;
    });

    const inferenceStats = getModelInferenceStats(data, modelName);
    const confidenceStats = getModelConfidenceStats(data, modelName);

    return {
      modelName,
      averageRating,
      totalQuestions,
      categoryCounts,
      inferenceStats,
      confidenceStats
    };
  });
}
